import { motion, useReducedMotion } from "framer-motion";
import { useTab } from "@/hooks/useTab";
import { useFilters } from "@/hooks/useFilters";
import { RankingRow } from "@/components/cards/RankingRow";
import { Badge } from "@/components/cards/Badge";
import { StreakHeatmap } from "@/components/charts/StreakHeatmap";
import { cn } from "@/lib/cn";
import { dateSpanish } from "@/lib/format";
import { medalLabel } from "@/lib/theme";
import { EmptyState, ErrorState, SectionTitle, TabSkeleton } from "./shared";
import type { StreakActiveRow, StreakHistoricalRow } from "@/api/types";

interface StreakHeroRowProps {
  row: StreakActiveRow;
  idx: number;
}

function StreakHeroRow({ row, idx }: StreakHeroRowProps) {
  const reducedMotion = useReducedMotion();
  const initial = (row.username ?? "?").charAt(0).toUpperCase();
  const rankIsMedal = row.rank > 0 && row.rank <= 3;
  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { duration: 0.35, ease: "easeOut", delay: idx * 0.07 }
      }
      className={cn(
        "flex items-center gap-4 rounded-[16px] border border-border-1 bg-white/[0.03] px-4 py-3 transition-colors",
        "hover:bg-white/[0.05] hover:border-border-2",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-10 items-center justify-center rounded-full text-xs font-bold tabular",
          rankIsMedal
            ? "bg-gradient-to-br from-brand-1 to-brand-3 text-white"
            : "bg-white/5 text-text-3",
        )}
      >
        {medalLabel(row.rank)}
      </div>
      <div
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-sm font-semibold text-text-2"
        aria-hidden
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-text-1">{row.username}</div>
        {row.team && <div className="truncate text-xs text-text-3">{row.team}</div>}
        {row.badges.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {row.badges.map((b) => (
              <Badge key={b} code={b} />
            ))}
          </div>
        )}
      </div>
      <div className="flex-shrink-0">
        <StreakHeatmap
          lastDay={row.last_day}
          streak={row.streak}
          weeks={12}
          showLegend={false}
        />
      </div>
      <div className="flex-shrink-0 text-right">
        <div className="tabular font-display text-3xl font-bold text-text-1">
          {row.streak}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-text-4">
          días seguidos
        </div>
      </div>
    </motion.div>
  );
}

function HistoricalRowCard({
  row,
  idx,
}: {
  row: StreakHistoricalRow;
  idx: number;
}) {
  const reducedMotion = useReducedMotion();
  const rankIsMedal = row.rank > 0 && row.rank <= 3;
  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { duration: 0.3, ease: "easeOut", delay: idx * 0.05 }
      }
      className={cn(
        "flex items-center gap-4 rounded-[14px] border border-border-1 bg-white/[0.02] px-4 py-3 transition-colors",
        "hover:bg-white/[0.04] hover:border-border-2",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-10 items-center justify-center rounded-full text-xs font-bold tabular",
          rankIsMedal
            ? "bg-gradient-to-br from-brand-1 to-brand-3 text-white"
            : "bg-white/5 text-text-3",
        )}
      >
        {medalLabel(row.rank)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-text-1">{row.username}</div>
        {row.team && <div className="truncate text-xs text-text-3">{row.team}</div>}
      </div>
      {row.badges.length > 0 && (
        <div className="flex flex-shrink-0 flex-wrap gap-1">
          {row.badges.map((b) => (
            <Badge key={b} code={b} />
          ))}
        </div>
      )}
      <div className="flex-shrink-0 text-right">
        <div className="tabular font-display text-2xl font-bold text-text-1">
          {row.streak}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-text-4">
          días
        </div>
      </div>
      {row.start_day && row.end_day && (
        <div className="flex-shrink-0 text-right">
          <div className="text-xs font-medium text-text-2">
            {dateSpanish(row.start_day)}
          </div>
          <div className="text-xs text-text-3">→ {dateSpanish(row.end_day)}</div>
        </div>
      )}
    </motion.div>
  );
}

/** Tab 3 — Current + historical streaks side-by-side. Top 5 active users
 * get a hero row with a GitHub-style heatmap; the tail renders as a
 * compact list. Historical streaks carry a big-number + date-range layout. */
export function StreaksTab() {
  const { filters } = useFilters();
  const { data, isPending, error } = useTab("streaks", filters);

  if (isPending) return <TabSkeleton />;
  if (error) return <ErrorState error={error} />;
  if (!data) return <EmptyState title="Sin datos." />;

  const topActive = data.active.slice(0, 5);
  const restActive = data.active.slice(5);

  return (
    <div className="grid grid-cols-2 gap-8">
      <section>
        <SectionTitle>Rachas activas</SectionTitle>
        {data.active.length === 0 ? (
          <EmptyState title="Ninguna racha activa ahora mismo." />
        ) : (
          <div className="flex flex-col gap-3">
            {topActive.map((row, idx) => (
              <StreakHeroRow key={row.uid} row={row} idx={idx} />
            ))}
            {restActive.length > 0 && (
              <div className="mt-2 flex flex-col gap-2">
                {restActive.map((row, idx) => (
                  <motion.div
                    key={row.uid}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.25,
                      ease: "easeOut",
                      delay: (topActive.length + idx) * 0.04,
                    }}
                  >
                    <RankingRow
                      rank={row.rank}
                      username={row.username}
                      team={row.team}
                      score={row.streak}
                      badges={row.badges}
                      metricLabel="días"
                      secondary={
                        row.last_day
                          ? { value: row.last_day, label: "último" }
                          : null
                      }
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section>
        <SectionTitle>Rachas más largas (histórico)</SectionTitle>
        {data.historical.length === 0 ? (
          <EmptyState title="Aún no hay rachas registradas." />
        ) : (
          <div className="flex flex-col gap-2">
            {data.historical.map((row, idx) => (
              <HistoricalRowCard
                key={`${row.uid}-${row.start_day ?? row.rank}`}
                row={row}
                idx={idx}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
